import * as React from 'react';
import { observer } from 'mobx-react';
import { EmployeeEvent, EmployeesEvents } from '../../types';
import LeaveTypes from '../LeaveTypes';
import { PlannedCell } from './PlannedCell';
import { LeaveTypesStore } from '../../store/LeaveTypesList';
import { differenceInDays, eachDayOfInterval, isWeekend } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { fetchTooltipDesc, handleEventType, handleTdClassName } from './Planner';
import { EmptyCell } from './EmptyCell';
import { UserStore } from '../../store/User';
import { PlannerStore } from '../../store/Planner';
import { Tooltip } from '@iba-team/portal-react-components/dist';
import { useTranslation } from 'react-i18next';
import { dateToYMDFormat } from '../../utils/dateHelper';
import { createPreciseDate } from '../../utils/formatDateAndTime';
import { ProjectManagementStore } from '../../store/ProjectManagement';
import { BusinessCell } from './BusinessCell';
import { JobSearchCell } from './JobSearchCell';
export interface EmployeeEventWithNewState{
  default: EmployeeEvent,
  new?: EmployeeEvent
}
interface Props{
  employeeData: EmployeesEvents,
  unPackageGroupedList: Date[],
}

export const ItemRow = observer((props: Props) => {
  const {allLeaveTypes} = LeaveTypesStore;
  const [eventList, setEventList] = useState<EmployeeEventWithNewState[]>([]);
  const [currentMode, setCurrentMode] = useState<'DELETE' | 'ADD' | undefined>();
  const {selectedRange, setSelectedRange, createPlannedAbsences, deletePlanningAbsences} = PlannerStore;
  const {profile} = UserStore;
  const isYourCell = props.employeeData.id === profile.id;
  const {t} = useTranslation();
  const {featureFlags} = ProjectManagementStore;

  const ALL_EVENTS = useMemo(() => {
    const sortedEvents: EmployeeEventWithNewState[] = props.employeeData.events.filter(e => handleEventType(e).type !== 'PLANNED');
    sortedEvents.push(...props.employeeData.events.filter(e => handleEventType(e).type === 'PLANNED'));
    return sortedEvents.map(evt => {
      const handled = handleEventType(evt);
      const range = eachDayOfInterval({start: createPreciseDate(handled.startDate), end: createPreciseDate(handled.endDate)});
      return range.map(interval => ({...evt, default: {...evt.default,  startDate: interval, endDate: interval, type: (evt.default?.type ? evt.default?.type : isWeekend(interval) ? 'HOLIDAY' : null) }, new: evt.new ? {...evt.new,  startDate: interval, endDate: interval} : undefined}));
    }).flat();
  }, [props.employeeData]);

  const groupedListWithEvents = useMemo(() => {
    return props.unPackageGroupedList.map(evt => {
      const event = ALL_EVENTS.find(e =>dateToYMDFormat(handleEventType(e).startDate) === dateToYMDFormat(evt) && props.employeeData.id === handleEventType(e).userId);
      if(event){
        return event;
      }
      else{
        return {
          default: {
            category: null,
            countryCode: null,
            endDate: new Date(evt),
            id: null,
            requestId: null,
            specialDayId: null,
            startDate: new Date(evt),
            status: null,
            type: isWeekend(evt) ? 'HOLIDAY' : null,
            userId: props.employeeData.id,
            originalTypeName: null,
          }
        };
      }
    });
  },[props.unPackageGroupedList]);

  useEffect(() => {
    setEventList(groupedListWithEvents);
  },[groupedListWithEvents]);

  const dateSetRequestBody = selectedRange.length === 2 ? eachDayOfInterval(
    {start: new Date(selectedRange[0]?.startDate), end: new Date(selectedRange[1]?.startDate)}
  ).map(i => dateToYMDFormat(i)) : [];
  const isElementInRange = (elem: EmployeeEventWithNewState) => (new Date(handleEventType(elem).startDate) >= new Date(selectedRange[0]?.startDate));
  const plannedCellClassName = (elem: EmployeeEventWithNewState) => {
    const prevChild = eventList.find(i => differenceInDays(new Date(handleEventType(elem).startDate), new Date(handleEventType(i).startDate)) === 1 && handleEventType(i).type === 'PLANNED');
    const nextChild = eventList.find(i => differenceInDays(new Date(handleEventType(elem).startDate), new Date(handleEventType(i).startDate)) === -1 && handleEventType(i).type === 'PLANNED');
    if(!selectedRange.length){
      if(prevChild && nextChild){
        return 'no-sides-border';
      }
      if(prevChild){
        return 'no-left-border';
      }
      if(nextChild){
        return 'no-right-border';
      }

      return 'default-border';
    }
  };

  const prepareOnClick = (event: EmployeeEventWithNewState, toggleMode: 'DELETE' | 'ADD') => {
    if(isYourCell && new Date(handleEventType(event).startDate) > new Date()){
      if(!currentMode){
        setCurrentMode(toggleMode);
      }
      if(!selectedRange.length){
        setSelectedRange([handleEventType(event)]);
      }
      if(selectedRange.length === 1){
        if(new Date(handleEventType(event).startDate) >= new Date(selectedRange[0]?.startDate)){
          setSelectedRange([...selectedRange, handleEventType(event)]);
        }
      }
    }
  };

  const renderCell = (elem: EmployeeEventWithNewState) => {
    const handlerElem = handleEventType(elem);
    const actionClassName = () => {
      if(isYourCell){
        if((new Date(handlerElem.startDate) <= new Date()) && !handlerElem.type && selectedRange.length){
          return 'disabled_cell';
        }
        if(selectedRange.length){
          if(isElementInRange(elem)){
            if(currentMode === 'DELETE'){
              return 'delete_state_cell_wrapper';
            }
            if(currentMode === 'ADD'){
              return 'add_state_cell_wrapper';
            }
          }
          return 'disabled_cell';
        }
      }
      else if(!handlerElem.type){
        return 'empty_another_cell';
      }
    };


    if(handlerElem?.type && handlerElem?.countryCode === null && handlerElem.originalTypeName){
      return isElementInRange(elem) ?
        <Tooltip title={fetchTooltipDesc(handlerElem, t)}>
          <span className={actionClassName()}>
            <LeaveTypes
              leaveTypes={allLeaveTypes}
              withoutText={true}
              type={handlerElem?.originalTypeName}
              infoMessageVisible={false}/>
          </span>
        </Tooltip>
        :
        <Tooltip title={fetchTooltipDesc(handlerElem, t)}>
          <span>
            <LeaveTypes
              leaveTypes={allLeaveTypes}
              withoutText={true}
              type={handlerElem?.originalTypeName}
              infoMessageVisible={false}/>
          </span>
        </Tooltip>;
    }
    if (handlerElem?.type === 'BusinessTrip' && featureFlags.SHOW_BUSINESS_TRIPS) {
      return <BusinessCell />;
    }
    if (handlerElem?.type === 'JobSearch') {
      return <JobSearchCell />;
    }
    if(handlerElem?.type === 'PLANNED'){
      return <PlannedCell
        isYourCell={isYourCell}
        currentMode={currentMode}
        plannedCellClassName={plannedCellClassName(elem)}
        setSelectedRange={(toggle) => prepareOnClick(elem,toggle)}
        selectedRange={selectedRange}
        actionClassName={actionClassName()}
        event={handlerElem}/>;
    }
    if((handlerElem?.type === 'WORKING') && (handlerElem?.countryCode !== null)){
      const rangeTooltip =
        <div>
          <>
            {fetchTooltipDesc(handlerElem, t)}
          </>
          <br/>
          Click to add planned absence
        </div>;

      return <Tooltip
        title={((!currentMode || currentMode === 'ADD') && isYourCell) && new Date(handlerElem.startDate) > new Date() ? rangeTooltip : fetchTooltipDesc(handlerElem, t)}>
        <div onClick={() => {
          prepareOnClick(elem, 'ADD');
        }
        } className={actionClassName()}>{handlerElem?.countryCode}</div>
      </Tooltip>;
    }
    if(handlerElem?.type && (handlerElem?.countryCode !== null)){
      return (
        <Tooltip title={fetchTooltipDesc(handlerElem, t)}>
          <div className={actionClassName()}>{handlerElem?.countryCode}</div>
        </Tooltip>);
    }
    if((handlerElem.type === 'HOLIDAY') || !handlerElem.type) {
      return (
        <EmptyCell
          isYourCell={isYourCell}
          currentMode={currentMode}
          setSelectedRange={(toggle) => prepareOnClick(elem, toggle)}
          selectedRange={selectedRange}
          event={handlerElem}
          actionClassName={actionClassName()}
        />);
    }
  };

  useEffect(() => {
    if(selectedRange.length === 1){
      if(currentMode === 'ADD'){
        setEventList(prev => prev.map(i => {
          if(!i.new){
            if(i.default.startDate === selectedRange[0]?.startDate){
              i = {new: {...selectedRange[0], type : 'PLANNED'}, default: i.default};
              return i;
            }
          }
          return i;
        }));
      }
    }
    if(selectedRange.length === 2){
      const range = eachDayOfInterval( {start: new Date(selectedRange[0].startDate), end: new Date(selectedRange[1].startDate)}).map(i => {
        // eslint-disable-next-line array-callback-return
        return eventList.find(el => {
          const handled = handleEventType(el);
          if(currentMode === 'ADD'){
            return dateToYMDFormat(handled.startDate) === dateToYMDFormat(i) && (!handled.type || handled.type === 'WORKING' || ((!handled.type || handled.type === 'HOLIDAY')  && isWeekend(new Date(handled.startDate))));
          }
          if(currentMode === 'DELETE'){
            return dateToYMDFormat(handled.startDate) === dateToYMDFormat(i) && handled.type === 'PLANNED';
          }
        });
      });
      range.forEach(i => {
        if(currentMode === 'ADD'){
          setEventList(prev => {
            return prev.map(pr => {
              if(!pr.new){
                if(pr.default.startDate === handleEventType(i!)?.startDate){
                  if(i){
                    pr = {new: {...i.default, type : 'PLANNED'}, default: pr.default};
                  }
                  return pr;
                }
                return pr;
              }
              return pr;
            });
          });
        }
        if(currentMode === 'DELETE'){
          setEventList(prev => {
            return prev.map(pr => {
              const handled = handleEventType(pr);
              // если pr уже имеет new,то удаляем его и берем default, если не имеет, то добавляем new
              if(handled.startDate === handleEventType(i!)?.startDate && handled.type === 'PLANNED'){
                if(pr.new){
                  pr = {new: undefined, default: {...pr.default}};
                  return pr;
                }
                return pr;
              }
              return pr;
            });
          });
        }
      });
      if(currentMode === 'ADD'){
        createPlannedAbsences(dateSetRequestBody);
      }
      if(currentMode === 'DELETE'){
        deletePlanningAbsences(dateSetRequestBody);
      }
      setCurrentMode(undefined);
      setSelectedRange([]);
    }
  },[selectedRange]);

  const renderBorder = (elem: EmployeeEventWithNewState) => {
    if(isElementInRange(elem) && currentMode) {
      return {
        border: 'none'
      };
    }

    return {
      borderLeft: '1px solid #E2E8F0',
      borderBottom: '1px solid #E2E8F0'
    };
  };

  return (
    <tr className={'user_events_list'} style={{position: selectedRange.length ? 'relative' : 'initial'}} onClick={() => {
      if(!isYourCell){
        setCurrentMode(undefined);
        setSelectedRange([]);
      }
    }
    }>
      {!isYourCell && selectedRange.length ?
        <div className={'list_blur_container'}>
        </div>
        : null
      }
      {eventList.map(elem =>
        <td style={renderBorder(elem)} className={`${handleTdClassName(handleEventType(elem),new Date(handleEventType(elem).startDate),false)}`}>
          {renderCell(elem)}
        </td>
      )}
    </tr>
  );
});
